import {
    SolveConnectorPlacementModelMode,
    ArticleGroupPlacementLifeCycleCode,
    ArticleInstancePlacementSourceType,
    type ArticleGroupPlacement,
    type SolveConnectorPlacementModel
} from 'api/models';
import {
    buildGroupSavedPrefabId,
    convertToDegree,
    isFramiClamp,
    mapArticleInstanceToPlacement
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { getConnectorRelatedPoiIndices } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { EMPTY_GUID } from 'shared/constants';

import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

export interface ExistingPlacementsResult {
    placements: ArticleGroupPlacement[];
    connectorPlacements: ArticleGroupPlacement[];
}

// Legacy instances lack poiGuids, so derive them from the POI-to-instance index.
function buildPoiGuidsByInstance(state: PlacementState): Map<string, string[]> {
    const poiGuidsByInstance = new Map<string, string[]>();
    for (const poiData of state.pois) {
        for (const instanceIds of Object.values(poiData.articles)) {
            for (const id of instanceIds) {
                const guids = poiGuidsByInstance.get(id) ?? [];
                if (!guids.includes(poiData.poi.poiGuid)) {
                    guids.push(poiData.poi.poiGuid);
                }
                poiGuidsByInstance.set(id, guids);
            }
        }
    }
    return poiGuidsByInstance;
}

export function buildExistingPlacements(
    state: PlacementState,
    poiIndices: number[],
    includeConnectorPlacements: boolean
): ExistingPlacementsResult {
    const poiDataList = poiIndices.map((index) => state.pois[index]).filter((poiData) => poiData !== undefined);
    if (poiDataList.length === 0) {
        return { placements: [], connectorPlacements: [] };
    }

    const placements: ArticleGroupPlacement[] = [];
    const connectorPlacements: ArticleGroupPlacement[] = [];
    const poiGuids = new Set(poiDataList.map((poiData) => poiData.poi.poiGuid));
    const articlesById = new Map<string, Set<string>>();
    let fallbackPoiGuidsByInstance: Map<string, string[]> | undefined;

    poiDataList.forEach((poiData) => {
        Object.entries(poiData.articles).forEach(([articleId, instanceIds]) => {
            const ids = articlesById.get(articleId) ?? new Set<string>();
            instanceIds.forEach((instanceId) => ids.add(instanceId));
            articlesById.set(articleId, ids);
        });
    });

    articlesById.forEach((instanceIdSet, articleId) => {
        const article = state.articles[articleId];
        if (!article || instanceIdSet.size === 0) {
            return;
        }
        const isConnector = isFramiClamp(article.articleNumber);
        if (isConnector && !includeConnectorPlacements) {
            return;
        }

        const instances = [...instanceIdSet]
            .map((instanceId) => {
                const articleInstance = article.articleInstances[instanceId];
                if (!articleInstance) {
                    return null;
                }

                const instancePoiGuids = articleInstance.poiGuids?.length
                    ? [...articleInstance.poiGuids]
                    : ((fallbackPoiGuidsByInstance ??= buildPoiGuidsByInstance(state)).get(instanceId) ?? []);
                if (!instancePoiGuids.some((guid) => poiGuids.has(guid))) {
                    return null;
                }

                const groupId = state.instanceToGroupMap[instanceId] ?? null;
                const groupName = groupId ? (state.articleGroups[groupId]?.name ?? null) : null;
                const sourceSavedGroupPrefabId = groupId
                    ? (buildGroupSavedPrefabId(state, groupId) ?? articleInstance.sourceSavedGroupPrefabId ?? null)
                    : (articleInstance.sourceSavedGroupPrefabId ?? null);

                let sourceType: ArticleInstancePlacementSourceType | null = null;
                if (groupId) {
                    sourceType = sourceSavedGroupPrefabId
                        ? ArticleInstancePlacementSourceType.SavedGroup
                        : ArticleInstancePlacementSourceType.UnsavedGroup;
                }

                const isLocked =
                    groupId !== null || sourceSavedGroupPrefabId !== null || articleInstance.isLocked === true;
                const placement = mapArticleInstanceToPlacement(instanceId, articleInstance, instancePoiGuids, false);

                return {
                    ...placement,
                    parts: placement.parts.map((part) => ({ ...part, rotation: convertToDegree(part.rotation) })),
                    isLocked,
                    sourceType,
                    sourceGroupId: groupId ?? articleInstance.sourceGroupId ?? null,
                    sourceGroupName: groupName ?? articleInstance.sourceGroupName ?? null,
                    sourceGroupInstanceId: articleInstance.sourceGroupInstanceId ?? null,
                    sourceSavedGroupPrefabId
                };
            })
            .filter((instance) => instance !== null);

        if (instances.length === 0) {
            return;
        }

        (isConnector ? connectorPlacements : placements).push({
            articleGuid: articleId,
            articleNumber: article.articleNumber,
            lifeCycleCode: article.lifeCycleCode as ArticleGroupPlacementLifeCycleCode,
            representationGuid: EMPTY_GUID,
            instances
        });
    });

    return { placements, connectorPlacements };
}

export interface BuildSolveConnectorPlacementRequestOptions {
    state: PlacementState;
    sessionId: string;
    systemId: string;
    poiIndex: number;
    manualSnapPoiIndex?: number;
    mode?: SolveConnectorPlacementModel['mode'];
    selectedConnectorArticleGuid?: string | null;
}

export function buildSolveConnectorPlacementRequest(
    options: BuildSolveConnectorPlacementRequestOptions
): SolveConnectorPlacementModel {
    const { state, sessionId, systemId, poiIndex } = options;

    const mode = options.mode ?? SolveConnectorPlacementModelMode.Automatic;
    const manualSnapPoiIndex =
        mode === SolveConnectorPlacementModelMode.Manual ? options.manualSnapPoiIndex : undefined;
    const placementsPoiIndex = manualSnapPoiIndex ?? poiIndex;
    const poiId = state.pois[placementsPoiIndex]?.poi.poiGuid ?? EMPTY_GUID;
    const relatedPoiIndices = getConnectorRelatedPoiIndices(state.pois, placementsPoiIndex);

    const { placements, connectorPlacements } = buildExistingPlacements(
        state,
        relatedPoiIndices,
        mode === SolveConnectorPlacementModelMode.Automatic
    );
    return {
        sessionId,
        poiId,
        systemId,
        existingPlacements: placements,
        existingConnectorPlacements: connectorPlacements,
        mode,
        selectedConnectorArticleGuid: options.selectedConnectorArticleGuid
    };
}
